/**
 * SOH bucket migration (history sankey): the chart's own SOH bucket list.
 *
 * Org meta: metaInfo.BatteryIntelligence.SOHAndRUL.charts.sohMigrationSankeyChart.buckets,
 * the same entry shape as BatteryIntelligence.stateOfHealthBuckets:
 *   { "label": "95-100", "range": { "min": 95, "max": 100 }, "color": "#1B7A42" }
 * plus an optional { "label": ..., "includeNull": true, "color": ... } entry for the
 * "SOH Unavailable" bucket's display label and colour.
 *
 * When the list has at least one range entry, the chart and its drill-down send the
 * range entries to the history endpoint as `sohBuckets`, which groups and filters by
 * them instead of the org-wide stateOfHealthBuckets. Without one, nothing is sent and
 * the chart uses the org-wide list (useSOHBuckets), as before.
 */
import { useMemo } from "react";
import { SOH_UNAVAILABLE_LABEL, isServerRangeEntry, isUnavailableEntry } from "./unavailableBucket";
import { buildSOHBuckets, useSOHBuckets } from "./sohBuckets";

/**
 * Limits the history endpoint puts on `sohBuckets`; a list over them is rejected with
 * 400. Mirror SOH_HISTORY_MAX_BUCKETS / SOH_HISTORY_MAX_BUCKET_LABEL_LENGTH in evcell-v1.1
 * app/services/soh_history_service.py (labels must also be unique); keep them in step.
 */
export const SOH_MIGRATION_MAX_BUCKETS = 20;
export const SOH_MIGRATION_MAX_BUCKET_LABEL_LENGTH = 64;

/** True when the range entries are within the backend's sohBuckets limits. */
export function isWithinSohBucketLimits(rangeEntries) {
  if (rangeEntries.length > SOH_MIGRATION_MAX_BUCKETS) return false;
  const labels = rangeEntries.map((entry) => entry.label);
  if (labels.some((label) => label.length > SOH_MIGRATION_MAX_BUCKET_LABEL_LENGTH)) return false;
  return new Set(labels).size === labels.length;
}

/**
 * Org meta `buckets` -> { displayBuckets, requestBuckets }, or null when the list has
 * no range entry the backend accepts, or breaks its limits (isWithinSohBucketLimits);
 * the caller then uses the org-wide list rather than a request that would fail.
 *   requestBuckets  [{ label, range: { min, max } }] in org meta order: the `sohBuckets`
 *                   request param. Only entries the backend reads as range branches
 *                   (isServerRangeEntry), so the request never carries an entry the
 *                   backend would reject; colour is display-only and left out.
 *   displayBuckets  useSOHBuckets-shaped list built from the same entries, so legend
 *                   labels and colours match the labels the backend returns.
 */
export function resolveSohMigrationBuckets(configBuckets) {
  const list = Array.isArray(configBuckets) ? configBuckets : [];
  const rangeEntries = list.filter((entry) => isServerRangeEntry(entry, SOH_UNAVAILABLE_LABEL));
  if (rangeEntries.length === 0 || !isWithinSohBucketLimits(rangeEntries)) return null;
  const unavailableEntry = list.find((entry) => isUnavailableEntry(entry, SOH_UNAVAILABLE_LABEL));
  return {
    requestBuckets: rangeEntries.map(({ label, range }) => ({
      label,
      range: { min: range.min ?? null, max: range.max ?? null },
    })),
    displayBuckets: buildSOHBuckets(unavailableEntry ? [...rangeEntries, unavailableEntry] : rangeEntries),
  };
}

/**
 * The sankey's buckets: { sohBuckets, requestBuckets }. sohBuckets is the display list
 * (the chart's own when configured, else the org-wide useSOHBuckets list);
 * requestBuckets is the `sohBuckets` request param, or null to send none.
 */
export function useSohMigrationBuckets(configBuckets) {
  const orgBuckets = useSOHBuckets();
  const resolved = useMemo(() => resolveSohMigrationBuckets(configBuckets), [configBuckets]);
  return useMemo(
    () => ({
      sohBuckets: resolved ? resolved.displayBuckets : orgBuckets,
      requestBuckets: resolved ? resolved.requestBuckets : null,
    }),
    [resolved, orgBuckets]
  );
}
